import { Model } from '@/clients/http'

import { ProductCategoryHttpParams, ProductCategoryResponses } from '@/types'

export class CategoryModel extends Model {
  // Define correlation keys for Redux state management
  public static readonly correlations = {
    GET_LIST: 'GET_CATEGORY_LIST',
    GET_BY_ID: 'GET_CATEGORY_BY_ID',
    CREATE: 'CREATE_CATEGORY',
    UPDATE: 'UPDATE_CATEGORY',
    DELETE: 'DELETE_CATEGORY',
  }

  // Define endpoint builders
  public static readonly endpoints = {
    getList: (props?: ProductCategoryHttpParams.GetList) =>
      this.createEndpoint({
        url: '/api/backoffice/category',
        params: props?.params,
        query: props?.query,
      }),

    getById: (props?: ProductCategoryHttpParams.GetById) =>
      this.createEndpoint({
        url: '/api/backoffice/category/:id',
        params: props?.params,
        query: props?.query,
      }),

    create: (props?: ProductCategoryHttpParams.Create) =>
      this.createEndpoint({
        url: '/api/backoffice/category',
        params: props?.params,
        query: props?.query,
      }),

    update: (props?: ProductCategoryHttpParams.Update) =>
      this.createEndpoint({
        url: '/api/backoffice/category/:id',
        params: props?.params,
        query: props?.query,
      }),

    delete: (props?: ProductCategoryHttpParams.Delete) =>
      this.createEndpoint({
        url: '/api/backoffice/category/:id',
        params: props?.params,
        query: props?.query,
      }),
  }

  /**
   * Get paginated list of categories
   * Backend returns: { success: true, message: "Done", data: { page, length, count, result: [...] } }
   * After unwrapping: response.data = { page, length, count, result: [...] }
   */
  public static readonly getList = async (props?: ProductCategoryHttpParams.GetList) => {
    return this.createHttpRequest<
      ProductCategoryResponses.GetList,
      ProductCategoryResponses.GetListError
    >({
      correlationKey: this.correlations.GET_LIST,
      payload: {
        method: 'get',
        url: this.endpoints.getList(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Get a single category by ID
   * Backend returns: { success: true, message: "Done", data: { result: {...} } }
   * After unwrapping: response.data = { result: {...} }
   */
  public static readonly getById = async (props: ProductCategoryHttpParams.GetById) => {
    return this.createHttpRequest<
      ProductCategoryResponses.GetById,
      ProductCategoryResponses.GetByIdError
    >({
      correlationKey: this.correlations.GET_BY_ID,
      payload: {
        method: 'get',
        url: this.endpoints.getById(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Create a new category
   */
  public static readonly create = async (props: ProductCategoryHttpParams.Create) => {
    return this.createHttpRequest<
      ProductCategoryResponses.Create,
      ProductCategoryResponses.CreateError
    >({
      correlationKey: this.correlations.CREATE,
      payload: {
        method: 'post',
        url: this.endpoints.create(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Update an existing category
   */
  public static readonly update = async (props: ProductCategoryHttpParams.Update) => {
    return this.createHttpRequest<
      ProductCategoryResponses.Update,
      ProductCategoryResponses.UpdateError
    >({
      correlationKey: this.correlations.UPDATE,
      payload: {
        method: 'put',
        url: this.endpoints.update(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Delete a category
   */
  public static readonly delete = async (props: ProductCategoryHttpParams.Delete) => {
    return this.createHttpRequest<
      ProductCategoryResponses.Delete,
      ProductCategoryResponses.DeleteError
    >({
      correlationKey: this.correlations.DELETE,
      payload: {
        method: 'delete',
        url: this.endpoints.delete(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }
}
